import React, { useState, useEffect } from 'react';
import { apiFetch, API_BASE } from '../utils/api';
// The client MIRROR of `server/helpers/poNumber.js`, held byte-identical by
// `server/tests/regressions/po-number-client-parity.test.js`. A convenience so
// the field shows what will be stored without waiting for a refetch — the
// server normalises independently and is the authority.
import { normalisePoNumber } from '../utils/poNumber';

/**
 * Inline editor for an order's PO Number. Renders "PO Number: [input] ✓ ✗"
 * with explicit save (tick) and cancel (cross) controls — no auto-save.
 *
 * - Tick saves via `PUT /api/orders/:id`. Visible only when the typed value
 *   differs from the order's current `po_number`.
 * - Cross reverts the input to the last saved value (or empty if no PO has
 *   been saved). Same visibility rule.
 * - Enter triggers save; Escape triggers cancel.
 *
 * Used wherever an order MANAGER should be able to update the PO Number
 * without expanding the order. This component deliberately knows nothing
 * about roles; the parent gates visibility.
 *
 * ⚠️ CORRECTED 2026-08-06. This said "Server gates the field to buyer/admin",
 * and it has not been true since tranche 3 increment 3b. The server resolves
 * `canManageDepartmentOrders` IN THE ORDER'S OWN DEPARTMENT. On the
 * `PUT /api/orders/:id` this component actually calls, that is `canSetPoNumber`
 * — `(await departmentAuthority(req, order.department_id)).canManageDepartmentOrders`
 * — with `canEditOrder` (server/helpers/editPermissions.js) answering the
 * separate "may you edit this order at all". The dedicated
 * `PATCH /api/orders/:id/po-number` uses `requireDepartmentCapability` with the
 * same capability and the same department. A correctly-created department buyer, holding
 * `department_assignments.role = 'buyer'` and NO unscoped `user_roles` row,
 * passes both and used to be shown nothing, because every parent gated on the
 * unscoped `isBuyer`. Parents now use `useOrderAuthority().canManageOrder`.
 *
 * Props:
 * - `order` — order object (must have `id` and optional `po_number`).
 * - `onSaved(orderId, normalisedValue)` — fires after a successful PUT so
 *   the parent can sync its own state (orders list, orderDetails cache, etc).
 * - `testId` — `data-testid` for the input. Differ per page.
 */
export default function InlinePoNumberEditor({ order, onSaved, testId }) {
  const savedValue = order.po_number || '';
  const [localValue, setLocalValue] = useState(savedValue);
  const [saving, setSaving] = useState(false);

  // Re-sync if the parent refetches the order and the PO changes externally.
  useEffect(() => {
    setLocalValue(order.po_number || '');
  }, [order.po_number]);

  const isDirty = String(localValue) !== String(savedValue);

  const handleSave = async () => {
    if (!isDirty || saving) return;
    /**
     * 🔀 2026-08-08 — the mirror of the server rule, replacing the old
     * `localValue === '' ? null : String(localValue)`.
     *
     * 🔑 **THE INPUT KEEPS SHOWING WHAT THE USER TYPED; ONLY WHAT IS SENT AND
     * REPORTED IS NORMALISED.** `localValue` is deliberately NOT written back
     * here. Normalising the field as they type would move the caret and fight
     * them mid-entry (type `1`, get `PO-1`, type `4` and where does it go?), and
     * normalising it on save would fire the `useEffect` resync a moment later
     * anyway once `order.po_number` updates. The field settles from the SERVER'S
     * value, which is the one that matters.
     *
     * 🪤 **AND THIS IS WHY THE INPUT IS `type="text"` AS OF THE SAME CHANGE.**
     * It was `type="number"`, which was defensible while every PO was digits —
     * and became a real bug the moment values carry a `PO-` prefix: a number
     * input cannot HOLD `PO-147`, so the stored value would render as an empty
     * box and a buyer would conclude their PO had been lost. The one live
     * 12-character value (`PO-BUYER-345`) was already unreachable through it.
     * See the input below.
     */
    const normalised = normalisePoNumber(localValue);
    setSaving(true);
    try {
      const res = await apiFetch(`${API_BASE}/api/orders/${order.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ po_number: normalised }),
      });
      if (!res.ok) {
        console.error('Failed to update PO number:', res.status);
        return;
      }
      if (onSaved) onSaved(order.id, normalised);
    } catch (err) {
      console.error('Failed to update PO number:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setLocalValue(savedValue);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  return (
    <span className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
      <span className="font-medium text-gray-700 dark:text-gray-300 w-24 flex-shrink-0">PO Number:</span>
      {/* 🚨 `type="text"`, NOT `type="number"` — CHANGED 2026-08-08 and it is a
          DEFECT FIX, not a preference. A number input refuses any value that is
          not a valid floating-point literal: with PO numbers now stored carrying
          a `PO-` prefix, `order.po_number` = `PO-147` would set `value` to a
          string the control cannot represent, the browser would render an EMPTY
          box, and a buyer looking at an order with a PO would see no PO. It was
          already wrong for the one 12-character value live in the dev database
          (`PO-BUYER-345`), which could never have been typed into it — the
          field was numeric while the column has always been free TEXT on both
          engines (`orders.po_number` is `TEXT` / `VARCHAR(100)`).
          ⚠️ Do not "restore" the number input for the spinner or the numeric
          keypad: the value is not a number and never was. */}
      <input
        type="text"
        // A numeric soft keyboard on a phone WITHOUT the numeric value
        // restriction — this is the mobile affordance `type="number"` was
        // really providing, kept without its defect. `inputMode` is a hint, so
        // letters stay typeable, which `PO-BUYER-345` needs.
        inputMode="text"
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="—"
        className="w-32 px-2 py-0.5 text-sm border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
        data-testid={testId}
        aria-label="PO Number"
        disabled={saving}
      />
      {isDirty && (
        <>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="p-1 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 disabled:opacity-50"
            data-testid={testId ? `${testId}-save` : undefined}
            aria-label="Save PO Number"
            title="Save (Enter)"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </button>
          <button
            type="button"
            onClick={handleCancel}
            disabled={saving}
            className="p-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50"
            data-testid={testId ? `${testId}-cancel` : undefined}
            aria-label="Cancel PO Number edit"
            title="Cancel (Esc)"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </>
      )}
    </span>
  );
}
